import _ from 'lodash';
import { AttachmentType, OwnershipStatus, getItemCategory } from '../config/constants';
import defaultSplitAttributes from '../config/defaultSplitAttributes';
import { Attributes } from '../config/dataAttributesIndex';
import slugger from '../slugger';

export default class Item {
  constructor(data) {
    this.id = data && data.id ? data.id : 0;
    this.stockId = data && data.stockId ? data.stockId : 0;
    this.category = data && data.category ? data.category : '';
    this.showStockImage = data && data.showStockImage ? data.showStockImage : false;

    // Construct default data structures for item and specific data sets
    this.itemData = _.mapValues(Attributes.COMMON, () => '');
    this.specificData = _.mapValues(Attributes[this.category], () => '');
    // Then merge in the (likely) sparse result set from constructor data
    if (data && data.itemData) {
      Object.assign(this.itemData, data.itemData);
    }
    if (data && data.specificData) {
      Object.assign(this.specificData, data.specificData);
    }

    this.itemRank = data && data.itemRank ? data.itemRank : '';
    this.valueGain = data && data.valueGain ? data.valueGain : 0;

    this.attachments = data && data.attachments ? data.attachments : [];
    this.stockImage = data && data.stockImage ? data.stockImage : {};
    this.stockImageThumbPath = data && data.stockImageThumbPath ? data.stockImageThumbPath : '';
    this.stockImageFullPath = data && data.stockImageFullPath ? data.stockImageFullPath : '';

    // Set some defaults
    if (!this.itemData.ownership_status) {
      this.itemData.ownership_status = OwnershipStatus['In Collection'];
    }

    if (!this.itemData.quantity) {
      this.itemData.quantity = 1;
    }
  }

  /**
   * Returns the subcategory key and value for the object, as a tuple.
   * For example, a watch would return ['w_brand', 'Armin Strom']
   * @returns string[] Form of [subcategoryKey, subcategoryValue]
   */
  getSubcategoryTypeAndKey() {
    const subcat = defaultSplitAttributes[this.category];
    return [subcat, this.specificData[subcat]];
  }

  getPermalink() {
    const [subcatType, subcatKey] = this.getSubcategoryTypeAndKey();
    return `/collections/${getItemCategory(this, true)}/${subcatType}/${slugger(subcatKey)}/${this.id}`;
  }

  hasConservation() {
    switch (this.category) {
      case 'ART':
        return true;

      case 'ANTIQUE':
        return true;

      case 'BOOK':
        return true;

      default:
        return false;
    }
  }

  hasArtist() {
    switch (this.category) {
      case 'ART':
        return true;

      default:
        return false;
    }
  }

  hasGrading() {
    switch (this.category) {
      case 'SPORTS_CARD':
        return true;

      case 'SPORTS_MEM':
        return true;

      case 'HISTORIC_MEM':
        return true;

      case 'MUSICAL_MEM':
        return true;

      case 'MOVIE_MEM':
        return true;

      case 'STAMP':
        return true;

      case 'COIN':
        return true;

      case 'CAR':
        return true;

      case 'WATCH':
        return true;

      case 'TOY':
        return true;

      case 'COMIC':
        return true;

      case 'WINE':
        return true;

      case 'HANDBAG':
        return true;

      case 'MUSICAL_INST':
        return true;

      default:
        return false;
    }
  }

  hasDetails() {
    switch (this.category) {
      case 'BOOK':
        return true;

      case 'STAMP':
        return true;

      case 'CAR':
        return true;

      case 'COMIC':
        return true;

      case 'WATCH':
        return true;

      default:
        return false;
    }
  }

  /**
   * Retrieves all images for an object, sorting first by main/featured image.
   * It folds in the stock image into the results, and sets the stock image as the main image
   * if there are no other overriding main images.
   * @returns object[] Form is: {
   *   id: Attachment ID
   *   originalFilename,
   *   isMainAttachment,
   *   path,
   *   thumbnailPath
   * }
   */
  getImages() {
    const allImages = this.attachments
      .filter(att => att.type === AttachmentType.IMAGE)
      .map(att => ({
        id: att.id,
        originalFileName: att.originalFileName,
        isMainAttachment: att.isMainAttachement,
        isStock: false,
        path: att.path,
        thumbnailPath: att.thumbnailPath,
      }));

    // Add in the stock image, setting it as main if there is no other main image set
    if (this.stockImage.path) {
      // exclude this image if it is the null image and we have other images
      // if (allImages.length === 0 || this.stockImage.filename !== 'null.jpg') {
      allImages.push({
        id: -1,
        originalFileName: this.stockImage.filename,
        isMainAttachment: !allImages.some(image => image.isMainAttachment),
        path: this.stockImage.path,
        isStock: true,
        thumbnailPath: this.stockImage.thumbnailPath,
      });
      // }
    }

    allImages.sort((a, b) => {
      if (a.isMainAttachment && !b.isMainAttachment) return -1;
      if (!a.isMainAttachment && b.isMainAttachment) return 1;
      return 0;
    });

    return allImages;
  }

  /**
   * Determines if the item is currently owned or not.
   */
  getIsOwned() {
    return (
      [
        OwnershipStatus.Gifted,
        OwnershipStatus['Lost/Stolen/Destroyed'],
        OwnershipStatus.Sold,
      ].indexOf(this.itemData.ownership_status) === -1
    );
  }
}

export const itemToApiMap = [
  ['id', 'id'],
  ['stockId', 'stockId'],
  ['category', 'category'],
  ['attachments', 'attachements'],
  ['showStockImage', 'showStockImage'],
  [
    'stockDetails',
    (apiObj) => {
      const stockInfo = apiObj.stockImage;
      if (!stockInfo) return {};

      return {
        // TODO: being lazy here, interpret better:
        stockImage: stockInfo,
        stockImageThumbPath: stockInfo.thumbnailPath,
        stockImageFullPath: stockInfo.path,
      };
    },
    (modelObj) => {
      return {
        stockImage: {
          category: modelObj.category,
          thumbnailPath: modelObj.stockImageThumbPath,
          path: modelObj.stockImageFullPath,
        },
      };
    },
  ],
  ['itemData', (apiObj) => {
    if (apiObj.specificData && apiObj.specificData.book_location_bookshelf) {
      if (apiObj.specificData.book_location_bookshelf === '1') {
        apiObj.specificData.book_location_bookshelf = 'Yes';
      } else {
        apiObj.specificData.book_location_bookshelf = 'No';
      }
    }

    return apiObj;
  },
  (modelObj) => {
    if (modelObj.specificData && modelObj.specificData.book_location_bookshelf) {
      if (modelObj.specificData.book_location_bookshelf === 'Yes') {
        modelObj.specificData.book_location_bookshelf = 1;
      } else {
        modelObj.specificData.book_location_bookshelf = 0;
      }
    }

    return modelObj;
  }],
  ['specificData', 'specificData'],
  ['valueGain', 'valueGain'],
  ['itemRank', 'itemRank'],
];



// WEBPACK FOOTER //
// ./src/models/Item.js